import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { HelpCircle, Check, Trash2, Eye, EyeOff, Star } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import RichTextEditor from '../../components/ui/rich-text-editor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminQA() {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [answeringId, setAnsweringId] = useState(null);
  const [answer, setAnswer] = useState('');

  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    try {
      const response = await axios.get(`${API}/qa?public_only=false`, { withCredentials: true });
      setQuestions(response.data);
    } catch (error) {
      toast.error('Failed to load questions');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = async (id) => {
    if (!answer.trim()) {
      toast.error('Please enter an answer');
      return;
    }
    try {
      await axios.put(`${API}/qa/${id}`, { 
        answer: answer,
        is_answered: true 
      }, { withCredentials: true });
      toast.success('Answer saved');
      setAnswer('');
      setAnsweringId(null);
      fetchQuestions();
    } catch (error) {
      toast.error('Failed to save answer');
    }
  };

  const handleTogglePublic = async (id, isPublic) => {
    try {
      await axios.put(`${API}/qa/${id}`, { is_public: isPublic }, { withCredentials: true });
      toast.success(isPublic ? 'Published to website' : 'Unpublished');
      fetchQuestions();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this question?')) return;
    try {
      await axios.delete(`${API}/qa/${id}`, { withCredentials: true });
      toast.success('Question deleted');
      fetchQuestions();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const openAnswer = (q) => {
    setAnsweringId(q.id);
    setAnswer(q.answer || '');
  };

  const filteredQuestions = questions.filter(q => {
    if (filter === 'answered') return q.is_answered;
    if (filter === 'unanswered') return !q.is_answered;
    if (filter === 'public') return q.is_public;
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="admin-qa-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-serif text-charcoal">Q&A</h1>
          <p className="text-charcoal/60">{questions.filter(q => !q.is_answered).length} questions need answers</p>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Questions</SelectItem>
            <SelectItem value="unanswered">Unanswered</SelectItem>
            <SelectItem value="answered">Answered</SelectItem>
            <SelectItem value="public">Published</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-xl p-4 border border-gold/10">
          <p className="text-2xl font-serif text-burgundy">{questions.filter(q => !q.is_answered).length}</p>
          <p className="text-sm text-charcoal/60">Need Answer</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gold/10">
          <p className="text-2xl font-serif text-olive">{questions.filter(q => q.is_answered).length}</p>
          <p className="text-sm text-charcoal/60">Answered</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gold/10">
          <p className="text-2xl font-serif text-gold">{questions.filter(q => q.is_public).length}</p>
          <p className="text-sm text-charcoal/60">Published</p>
        </div>
      </div>

      <div className="space-y-4">
        {filteredQuestions.map((q) => (
          <motion.div
            key={q.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`bg-white rounded-xl p-6 border ${!q.is_answered ? 'border-burgundy/30' : 'border-gold/10'}`}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <HelpCircle size={18} className={q.is_answered ? 'text-olive' : 'text-burgundy'} />
                  <span className="text-sm text-charcoal/50">From: {q.asker_name || 'Anonymous'}</span>
                  {q.is_answered && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-olive/10 text-olive">Answered</span>
                  )}
                  {q.is_public && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-gold/10 text-gold">Published</span>
                  )}
                </div>
                <p className="text-xs text-charcoal/40">
                  {new Date(q.created_at).toLocaleDateString()}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {q.is_answered && (
                  <button
                    onClick={() => handleTogglePublic(q.id, !q.is_public)}
                    className={`p-2 ${q.is_public ? 'text-gold' : 'text-charcoal/50 hover:text-gold'}`}
                    title={q.is_public ? 'Unpublish' : 'Publish'}
                  >
                    {q.is_public ? <Eye size={18} /> : <EyeOff size={18} />}
                  </button>
                )}
                <button
                  onClick={() => handleDelete(q.id)}
                  className="p-2 text-charcoal/50 hover:text-red-500"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
            
            {/* Question */}
            <div className="bg-cream/50 rounded-lg p-4 mb-4">
              <p className="font-medium text-charcoal">{q.question}</p>
            </div>
            
            {/* Existing Answer */}
            {q.answer && answeringId !== q.id && (
              <div className="bg-olive/5 rounded-lg p-4 mb-4">
                <p className="text-sm text-charcoal/50 mb-2">Answer:</p>
                <div className="text-charcoal/80" dangerouslySetInnerHTML={{ __html: q.answer }} />
              </div>
            )}
            
            {/* Answer Form */}
            {answeringId === q.id ? (
              <div className="space-y-4 mt-4 pt-4 border-t border-gold/10">
                <div>
                  <Label>Your Answer</Label>
                  <RichTextEditor
                    value={answer}
                    onChange={setAnswer}
                    placeholder="Write your answer..."
                    height="150px"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleAnswer(q.id)}
                    className="bg-burgundy hover:bg-purple"
                  >
                    <Check size={16} className="mr-2" /> Save Answer
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => { setAnsweringId(null); setAnswer(''); }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                variant="outline"
                onClick={() => openAnswer(q)}
                className="mt-2"
              >
                {q.answer ? 'Edit Answer' : 'Write Answer'}
              </Button>
            )}
          </motion.div>
        ))}
        
        {filteredQuestions.length === 0 && (
          <div className="text-center py-12 text-charcoal/50">
            <HelpCircle size={48} className="mx-auto mb-4 opacity-20" />
            <p>No questions found</p>
          </div>
        )}
      </div>
    </div>
  );
}
